import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import type {
  DashboardFinancePeriodParams,
  DashboardPeriod,
} from './finance.api'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_DASHBOARD')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export interface ClinicGoal {
  clinic_goal_id: string
  clinic_id: string
  period: DashboardPeriod
  gross_production_goal: number
  net_production_goal: number
  gross_production_chart_goal: number
  gross_collection_goal: number
  gross_collection_chart_goal: number
  collection_header_goal: number
  collection_chart_goal: number
  collection_rate_target_pct: number
  patient_collection_dollar_goal: number
  insurance_collection_dollar_goal: number
  patient_days_to_collect_target: number
  insurance_days_to_collect_target: number
  practice_gross_collection_goal: number
  practice_net_collection_goal: number
  net_profit_goal: number
  practice_revenue_trend_goal: number
  ar_aging_balance_goal: number
  ar_over_90_balance_goal: number
  dentist_production_goal: number
  hygienist_production_goal: number
  production_per_hygiene_visit_goal: number
  net_hygiene_production_goal: number
  insurance_collection_share_goal_pct?: number | null
  patient_collection_share_goal_pct?: number | null
  dentist_production_share_goal_pct?: number | null
  hygienist_production_share_goal_pct?: number | null
  treatment_value_billed_goal?: number | null
  treatment_value_collected_goal?: number | null
  treatment_value_potential_goal?: number | null
  outstanding_balance_goal?: number | null
  claims_under_review_goal?: number | null
  claims_pending_goal?: number | null
  claims_paid_goal?: number | null
  claims_denied_goal?: number | null
  production_by_procedure_goal?: number | null
  production_per_provider_goal?: number | null
  patients_seen_goal: number
  chair_utilization_goal_pct?: number | null
  date_time_created_at?: string
  date_time_updated_at?: string
}

export const getClinicGoal = (
  params: DashboardFinancePeriodParams,
  options?: { signal?: AbortSignal },
): Promise<ClinicGoal | null> =>
  axios
    .get<
      ApiResponse<{ clinic_goal: ClinicGoal | null }>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/clinic-goals`, { params, signal: options?.signal })
    .then((response) => response.data.data.clinic_goal ?? null)

/** Editable numeric goal fields (everything except identity / audit columns). */
export type ClinicGoalValues = Partial<
  Omit<
    ClinicGoal,
    | 'clinic_goal_id'
    | 'clinic_id'
    | 'period'
    | 'date_time_created_at'
    | 'date_time_updated_at'
  >
>

export type CreateClinicGoalInput = ClinicGoalValues & {
  clinic_id: string
  period: DashboardPeriod
}

export type UpdateClinicGoalInput = ClinicGoalValues & {
  clinic_goal_id: string
}

export const createClinicGoal = (
  payload: CreateClinicGoalInput,
): Promise<ClinicGoal> =>
  axios
    .post<
      ApiResponse<{ clinic_goal: ClinicGoal }>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/clinic-goals`, payload)
    .then((response) => response.data.data.clinic_goal)

export const updateClinicGoal = (
  payload: UpdateClinicGoalInput,
): Promise<ClinicGoal> =>
  axios
    .put<
      ApiResponse<{ clinic_goal: ClinicGoal }>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/clinic-goals`, payload)
    .then((response) => response.data.data.clinic_goal)
